import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { db } from '../lib/firebase.js'
import { onValue, ref } from 'firebase/database'
import { fmtVsPar, getPlayStyle, headToHeadStatus, matchPlayResult, netAvailableFor, netScoresFor, parsFor, rankTeams, strokeIndexFor, totalPar } from '../lib/scoring.js'
import CLUB from '../config/club.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { pickPremiumCurrentEventId } from '../lib/pickCurrentEvent.js'
import { boardTitle } from '../lib/boardTitle.js'
import { TV_BG_GREEN, TV_CARD_WHITE, TV_CARD_TEXT } from '../config/tvTheme.js'
import PageNav from '../components/PageNav.jsx'
import { lastNames } from '../lib/teamName.js'
import TeamCupLeaderboard from './TeamCupLeaderboard.jsx'
import TvScroll from './TvScroll.jsx'
import ScrambleTable from './ScrambleTable.jsx'

const REFRESH_MS = 30000

// ── PBCC bracket brand (matches TvBracketGrid exactly) ────────────────────
const PBCC_GREEN_BG = PBCC_PREMIUM ? '#0E281D' : '#123D26'   // dark green board background
const PBCC_CREAM    = PBCC_PREMIUM ? '#F7F2E6' : '#FFFFFF'   // card fill
const PBCC_GOLD     = PBCC_PREMIUM ? '#C2A24E' : '#FFFFFF'   // titles / headers / chips
const PBCC_CRIMSON  = '#B10C0C'   // team names
const PBCC_FONT     = 'Marcellus, Georgia, serif'
// TV card edge: premium/TEST cream cards get a thin GOLD hairline on the deep
// green — never the old light-gray outline that read as a green tint. Club/live
// keep their original light-gray TV edge.
const TV_CARD_EDGE  = PBCC_PREMIUM ? '#C2A24E80' : '#E5E7EB'
// Text that sits ON the cream card is DARK GREEN, not gold — gold is too
// light to read on cream. Gold stays only for accents (card border, the
// header underline, the leader-row tint) and for header text on the
// dark-green page background. Names stay crimson; the ▲/▼/◆ symbols carry
// the leader/trailer cue.
const CARD_TITLE_GREEN = '#123D26'            // flight names on cream
const CARD_SUB_GREEN   = 'rgba(18, 61, 38,0.62)' // sublabels + column headers
const STATUS_GREEN     = '#0A5A2A'            // active status + FINAL banner
const STATUS_MUTED     = '#6B7A63'            // "Not started" / no opponent

export default function LeaderboardApp({ tournamentIdProp = null, tv = false, embedded = false } = {}) {
  const [params] = useSearchParams()
  // tournamentIdProp wins (lets /testleaderboard pin to its auto-discovered
  // test scorecard without rewriting the URL); falls back to ?t= then the
  // most-recent open non-demo tournament.
  const tournamentId = tournamentIdProp ?? params.get('t')
  const [data, setData] = useState(null)
  const [allTournaments, setAllTournaments] = useState({})
  const [useNet, setUseNet] = useState(false)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const off = onValue(ref(db, 'tournaments'), (snap) => setAllTournaments(snap.val() || {}))
    return () => off()
  }, [])

  // Pick which tournament to show. Demos are excluded from auto-pick (they
  // appear in the leaderboard only when explicitly requested via ?t=…).
  const activeId = useMemo(() => {
    if (tournamentId) return tournamentId
    // TEST: deterministic, flights/stage-aware pick — never the empty qualifying.
    if (PBCC_PREMIUM) return pickPremiumCurrentEventId(allTournaments)
    const entries = Object.entries(allTournaments).filter(([, v]) => !v?.info?.isDemo)
    if (entries.length === 0) return null
    entries.sort((a, b) => (b[1]?.info?.createdAt || 0) - (a[1]?.info?.createdAt || 0))
    return entries[0][0]
  }, [tournamentId, allTournaments])

  useEffect(() => {
    if (!activeId) {
      setData(null)
      return
    }
    const off = onValue(ref(db, `tournaments/${activeId}`), (snap) => {
      const v = snap.val()
      setData(v)
      // eslint-disable-next-line no-console
      console.log('[leaderboard] tournament', activeId, 'playStyle:', v?.info?.playStyle || '(default match)')
    })
    return () => off()
  }, [activeId])

  // Follow the admin's persisted GROSS/NET display mode (tvSettings.netMode).
  // The public page defaults to it (spectators can still flip locally until the
  // admin changes it again); the TV board has no toggle, so it always tracks it.
  const persistedNet = data?.tvSettings?.netMode
  useEffect(() => { setUseNet(!!persistedNet) }, [persistedNet])

  // Force a re-render every 30s for any time-based UI (the data itself updates live via onValue).
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), REFRESH_MS)
    return () => clearInterval(id)
  }, [])

  if (!activeId) {
    return <BoardEmpty msg="No tournaments yet. Create one in the admin portal." />
  }
  if (!data) return <BoardEmpty msg="Loading tournament…" />

  const flights = Object.entries(data.flights || {}).map(([id, v]) => ({ id, ...v }))
  const pars = parsFor(data)
  const courseTotalPar = totalPar(pars)
  const playStyle = getPlayStyle(data, null)
  const isMatch = playStyle === 'match'

  // Team Cup gets its own leaderboard view — branch before rendering the
  // standard 4-Ball / Stroke / Match layouts.
  if (data.info?.format === 'Team Cup') {
    const players = Object.entries(data.players || {}).map(([id, v]) => ({ id, ...v }))
    return (
      <TeamCupLeaderboard
        tournament={data}
        tournamentId={activeId}
        pars={pars}
        players={players}
        tv={tv}
      />
    )
  }

  const scrollSpeed = data?.tvSettings?.scrollSpeed || 'slow'
  // Per-hole NET scoring (STEP 4, auto): the toggle appears whenever net data
  // exists (teams carry playingHandicaps — written by the flight-lock snapshot,
  // or pre-stamped by a demo). GROSS is the default; on the TV there's no
  // toggle, so useNet stays false → TV always renders GROSS. Gross is identical.
  const netAvailable = netAvailableFor(data)
  const strokeIndex = strokeIndexFor(data)
  const cards = flights.map((f) => (
    <FlightBoard
      key={f.id}
      flight={f}
      useNet={useNet}
      netMode={netAvailable && useNet}
      strokeIndex={strokeIndex}
      pars={pars}
      playStyle={getPlayStyle(data, f)}
      tournamentInfo={data.info}
      tv={tv}
    />
  ))

  // ── TV screen: full-screen (or embedded preview tile), auto-scrolls
  //    through every flight, no page chrome (no PageNav / GROSS-NET / QR). ──
  if (tv) {
    const container = embedded ? 'relative w-full h-full overflow-hidden' : 'fixed inset-0 overflow-hidden'
    return (
      <div className={`${container} flex flex-col`} style={{ backgroundColor: TV_BG_GREEN, fontFamily: PBCC_FONT }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
        <header className={`shrink-0 flex items-center gap-4 ${embedded ? 'px-5 pt-4 pb-2' : 'px-8 pt-6 pb-3'}`}
                style={{ borderBottom: `1px solid ${PBCC_GOLD}44` }}>
          <img src={CLUB.logoLight} alt={CLUB.name}
               style={{ height: embedded ? 60 : 96, objectFit: 'contain', display: 'block' }} />
          <div className="min-w-0">
            <div className={`uppercase tracking-[0.25em] font-bold ${embedded ? 'text-sm' : 'text-sm'}`} style={{ color: PBCC_GOLD }}>
              Live Leaderboard
            </div>
            <h1 className={`font-bold leading-tight truncate ${embedded ? 'text-xl' : 'text-4xl'}`} style={{ color: PBCC_GOLD }}>
              {boardTitle(data.info?.name)}
            </h1>
          </div>
        </header>
        <div className="flex-1 min-h-0">
          {flights.length === 0
            ? <div className="h-full flex items-center justify-center" style={{ color: PBCC_GOLD }}>No flights configured.</div>
            : <TvScroll disabled={embedded} speed={scrollSpeed} innerClassName="px-8 py-6" style={{ fontFamily: PBCC_FONT }}>
                {/* TEST TV: this board renders inside TvStage's FIXED 1920×1080
                    canvas, but Tailwind's xl:/lg: breakpoints key off the REAL
                    viewport — so a narrow (~1280) TV collapsed the 3-col grid to
                    1. Force 3 columns from the canvas width, independent of the
                    device viewport, so the TV matches a wide desktop. Club keeps
                    the responsive classes (it doesn't render inside TvStage). */}
                <div
                  className={`grid gap-4 ${PBCC_PREMIUM ? '' : 'xl:grid-cols-3 lg:grid-cols-2'}`}
                  style={PBCC_PREMIUM ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } : undefined}>
                  {cards}
                </div>
              </TvScroll>}
        </div>
      </div>
    )
  }

  // ── /leaderboard page: brand restyle, keeps its manual scroll. ───────────
  return (
    <div className="min-h-full relative" style={{ backgroundColor: PBCC_GREEN_BG, fontFamily: PBCC_FONT }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
      <header className={embedded ? 'px-1 pt-1 pb-3' : 'px-8 pt-6 pb-4'}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            {/* Embedded in the admin tab: the tournament name/course header
                already sits above the tab bar, so skip the sponsor logo + title
                block (and the site PageNav below) to avoid duplicate chrome. */}
            {!embedded ? (
              <>
                <SponsorSlot />
                <div>
                  <div className="uppercase tracking-widest text-sm" style={{ color: PBCC_GOLD }}>Live Leaderboard</div>
                  <h1 className="text-4xl font-bold" style={{ color: PBCC_GOLD }}>{data.info?.name}</h1>
                  <p className="text-sm" style={{ color: `${PBCC_CREAM}cc` }}>
                    {data.info?.course} · {data.info?.date} · {data.info?.format || '4-Ball Best Ball'} · Par {courseTotalPar}
                  </p>
                </div>
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-4">
            {/* GROSS/NET: shown for stroke as before, and for any play style
                once net data exists (auto-snapshot). Default GROSS. */}
            {(netAvailable || !isMatch) ? (
              <div className="flex items-center gap-1 rounded-lg p-1"
                   style={{ backgroundColor: `${PBCC_GOLD}14`, border: `1px solid ${PBCC_GOLD}55` }}>
                {[{ on: false, label: 'GROSS' }, { on: true, label: 'NET' }].map((o) => {
                  const active = useNet === o.on
                  return (
                    <button
                      key={o.label}
                      type="button"
                      onClick={() => setUseNet(o.on)}
                      className="rounded-md px-3 py-1 text-sm font-bold uppercase tracking-wider transition"
                      style={active
                        ? { backgroundColor: PBCC_GOLD, color: PBCC_GREEN_BG }
                        : { backgroundColor: 'transparent', color: PBCC_GOLD }}>
                      {o.label}
                    </button>
                  )
                })}
              </div>
            ) : null}
            {!embedded ? <PageNav tone="light" absolute={false} /> : null}
          </div>
        </div>
      </header>

      <main className={embedded ? 'px-1 pb-4' : 'px-8 pb-8'}>
        {flights.length === 0 ? (
          <div className="rounded-2xl p-10 text-center" style={{ backgroundColor: `${PBCC_CREAM}14`, color: `${PBCC_CREAM}cc` }}>No flights configured.</div>
        ) : (
          <div className={`grid gap-4 ${flights.length > 2 ? 'lg:grid-cols-2 xl:grid-cols-3' : 'lg:grid-cols-2'}`}>
            {cards}
          </div>
        )}
        <ContestStrip contests={data.contests} />
      </main>
    </div>
  )
}

function SponsorSlot() {
  return (
    <div className="flex items-center justify-center">
      <img
        src={CLUB.logoLight}
        alt={CLUB.name}
        style={{ height: '92px', objectFit: 'contain', display: 'block' }}
      />
    </div>
  )
}

function FlightBoard({ flight, useNet, netMode = false, strokeIndex, pars, playStyle, tournamentInfo, tv = false }) {
  // Scramble flights (e.g. Flight 12) render the score table, never VS pairings
  // or a stroke ranking — this short-circuits before any playStyle branching.
  if (flight.scramble) return <ScrambleTable data={flight.scramble} flightName={flight.info?.name} />
  // Boolean flag only — never a color. The color tokens are module constants
  // referenced directly here, so nothing can be "not defined".
  const cardBg = tv ? TV_CARD_WHITE : PBCC_CREAM
  const rawTeams = Object.entries(flight.teams || {}).map(([id, v]) => ({ id, ...v }))
  // Per-hole NET mode: swap each team's stored gross scores for net best-ball
  // scores (same shape), then run the EXACT SAME ranking / match logic on them.
  const teams = netMode ? rawTeams.map((t) => ({ ...t, scores: netScoresFor(t, strokeIndex) })) : rawTeams
  if (playStyle === 'match') return <MatchFlightBoard flight={flight} teams={teams} tv={tv} />
  // In net mode the strokes are already baked into `scores`, so rank by vs-par
  // (useNet=false); otherwise keep the legacy flat-handicap GROSS/NET behavior.
  const ranked = rankTeams(teams, netMode ? false : useNet, pars)
  // Show a dedicated NET column whenever the tournament has scramble
  // handicaps enabled — most users want to see both gross and net at the
  // same time during a 2-man scramble. Falls back to the user's GROSS/NET
  // toggle for non-handicap stroke tournaments.
  const isScrambleHcp =
    tournamentInfo?.format === 'Scramble' && !!tournamentInfo?.scrambleHandicapsEnabled
  return (
    <div className={`rounded-2xl overflow-hidden ${tv ? '' : 'shadow-2xl'}`} style={{ backgroundColor: cardBg, border: `1px solid ${tv ? TV_CARD_EDGE : `${PBCC_GOLD}55`}`, fontFamily: PBCC_FONT }}>
      <div className="px-5 py-3 flex justify-between items-center" style={{ borderBottom: `1px solid ${PBCC_GOLD}44` }}>
        <h2 className="font-bold text-lg uppercase tracking-[0.14em]" style={{ color: tv ? TV_CARD_TEXT : CARD_TITLE_GREEN }}>{flight.info?.name}</h2>
        <span className="text-sm uppercase tracking-widest" style={{ color: tv ? TV_CARD_TEXT : CARD_SUB_GREEN }}>{flight.info?.format || '4-Ball Best Ball'} · Stroke</span>
      </div>
      {ranked.length === 0 ? (
        <div className="p-6 text-center" style={{ color: tv ? TV_CARD_TEXT : `${PBCC_CRIMSON}88` }}>No teams yet.</div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="text-sm uppercase tracking-widest border-b" style={{ color: tv ? TV_CARD_TEXT : CARD_SUB_GREEN, borderColor: `${PBCC_GOLD}33` }}>
              <th className="text-left px-4 py-2 w-10">#</th>
              <th className="text-left px-4 py-2">Team</th>
              <th className="text-right px-2 py-2">Thru</th>
              <th className="text-right px-2 py-2">Gross</th>
              {isScrambleHcp ? <th className="text-right px-2 py-2">Net</th> : null}
              <th className="text-right px-4 py-2">vs Par</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((r, i) => {
              const isFinal = r.thru >= 18
              return (
                <tr key={r.id} className="border-b last:border-0"
                    style={{ borderColor: `${PBCC_GOLD}22`, backgroundColor: tv ? 'transparent' : (i === 0 ? `${PBCC_GOLD}1f` : i % 2 ? 'rgba(18, 61, 38,0.05)' : 'transparent') }}>
                  <td className="px-4 py-3 font-extrabold text-2xl" style={{ color: tv ? TV_CARD_TEXT : PBCC_CRIMSON }}>{i + 1}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-base" style={{ color: tv ? TV_CARD_TEXT : PBCC_CRIMSON }}>{lastNames(r.info?.name)}</div>
                    <div className="text-sm" style={{ color: tv ? TV_CARD_TEXT : '#5B5B5B' }}>{(r.info?.players || []).join(' · ')}</div>
                  </td>
                  <td className="px-2 py-3 text-right font-semibold" style={{ color: tv ? TV_CARD_TEXT : PBCC_GREEN_BG }}>
                    {isFinal ? (
                      <span className="inline-flex items-center rounded-md px-2 py-0.5 text-sm font-extrabold uppercase tracking-wider"
                            style={{ backgroundColor: '#0a5a2a', color: '#fff' }}>
                        FINAL
                      </span>
                    ) : r.thru}
                  </td>
                  <td className="px-2 py-3 text-right font-extrabold text-lg" style={{ color: tv ? TV_CARD_TEXT : PBCC_GREEN_BG }}>{r.gross || '—'}</td>
                  {isScrambleHcp ? (
                    <td className="px-2 py-3 text-right font-extrabold text-lg" title={`Hcp ${r.hcpStrokes}`} style={{ color: tv ? TV_CARD_TEXT : PBCC_GREEN_BG }}>
                      {r.gross > 0 ? r.net : '—'}
                    </td>
                  ) : null}
                  <td className="px-4 py-3 text-right font-extrabold text-lg" style={{ color: tv ? TV_CARD_TEXT : PBCC_GREEN_BG }}>{fmtVsPar(r.vsPar)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

function MatchFlightBoard({ flight, teams, tv = false }) {
  // Own local cardBg (boolean flag + directly-referenced module tokens) — this
  // component draws its OWN card, so it must NOT rely on FlightBoard's cardBg.
  const cardBg = tv ? TV_CARD_WHITE : PBCC_CREAM
  // TEST TV: rebuilt clean flight card — a ranked POS | TEAM | RESULT table.
  // Club/live and the public /leaderboard keep the original head-to-head rows.
  if (tv && PBCC_PREMIUM) return <PremiumFlightCard flight={flight} teams={teams} cardBg={cardBg} />
  const matches = buildMatches(teams)
  return (
    <div className={`rounded-2xl overflow-hidden ${tv ? '' : 'shadow-2xl'}`} style={{ backgroundColor: cardBg, border: `1px solid ${tv ? TV_CARD_EDGE : `${PBCC_GOLD}55`}`, fontFamily: PBCC_FONT }}>
      <div className="px-5 py-3" style={{ borderBottom: `1px solid ${PBCC_GOLD}44` }}>
        <h2 className="font-bold text-lg uppercase tracking-[0.14em]" style={{ color: tv ? TV_CARD_TEXT : CARD_TITLE_GREEN }}>{flight.info?.name}</h2>
      </div>
      {matches.length === 0 ? (
        <div className="p-6 text-center" style={{ color: tv ? TV_CARD_TEXT : `${PBCC_CRIMSON}88` }}>No matches yet.</div>
      ) : (
        <table className="w-full">
          {/* Column widths kept aligned via colgroup now that the header row
              (# / Team A / Status / Team B labels) is gone. */}
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: '41%' }} />
            <col />
            <col style={{ width: '41%' }} />
          </colgroup>
          <tbody>
            {matches.map((m, i) => <MatchRow key={m.key} index={i} match={m} tv={tv} />)}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── TEST TV: minimal flight card — two teams per match ─────────────────────
// While a match is LIVE, both teams show and only the LEADER is marked (bold
// green ▲). When FINAL, only the WINNER is marked (bold green 🏆) and the loser
// dims. NO margins (5&4 / X UP) and NO losing-team scores — just the matchup.
// Cream card, gold hairline, deep-green header rule.
function PremiumFlightCard({ flight, teams, cardBg }) {
  const matches = buildMatches(teams)            // teamA oriented as leader/winner
  const real = matches.filter((m) => m.teamB)
  const flightComplete = real.length > 0 && real.every((m) => m.result?.final)
  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: cardBg, border: `1px solid ${TV_CARD_EDGE}`, fontFamily: PBCC_FONT }}>
      <div className="px-5 py-3 flex items-center justify-between gap-3" style={{ borderBottom: `1px solid ${PBCC_GOLD}44` }}>
        <h2 className="font-bold text-lg uppercase tracking-[0.14em] truncate" style={{ color: TV_CARD_TEXT }}>{flight.info?.name}</h2>
        {flightComplete ? (
          <span className="shrink-0 text-sm font-black uppercase tracking-wider px-2.5 py-1 rounded"
                style={{ backgroundColor: '#0A5A2A', color: '#F7F2E6' }}>Complete</span>
        ) : null}
      </div>
      {matches.length === 0 ? (
        <div className="p-6 text-center" style={{ color: TV_CARD_TEXT }}>No matches yet.</div>
      ) : (
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '37%' }} />
            <col />
            <col style={{ width: '37%' }} />
          </colgroup>
          <tbody>
            {matches.map((m) => <PremiumMatchRow key={m.key} match={m} />)}
          </tbody>
        </table>
      )}
    </div>
  )
}

function PremiumMatchRow({ match }) {
  const { teamA, teamB, leaderGap, played, result } = match
  const GREEN = '#0A5A2A'
  const DIM = '#8C8C8C'
  if (!teamB) {
    return (
      <tr className="border-b last:border-0" style={{ borderColor: `${PBCC_GOLD}22` }}>
        <td colSpan={3} className="py-2.5 px-4 text-center font-bold text-base" style={{ color: DIM }}>
          {lastNames(teamA.info?.name)} — no opponent
        </td>
      </tr>
    )
  }
  const NEUTRAL = '#5B6B5B'
  const isFinal = !!result?.final
  const allSquare = leaderGap === 0
  const notStarted = played === 0
  // teamA is oriented as the leader/winner. Emphasize it only when there's an
  // actual leader (live, not all-square) or the match is decided (final).
  const aEmph = isFinal || (!notStarted && !allSquare)
  const marker = isFinal ? '🏆' : (aEmph ? '▲' : '')
  // Center: FINAL shows NO score (winner + trophy carries it); LIVE keeps the
  // live "X UP THRU Y" (or ALL SQUARE); not-started is a plain divider.
  let center = 'vs', centerColor = '#9B9B8F'
  if (isFinal) { center = '' }
  else if (notStarted) { center = 'vs' }
  else if (allSquare) { center = `ALL SQUARE THRU ${played}`; centerColor = NEUTRAL }
  else { center = `${leaderGap} UP THRU ${played}`; centerColor = GREEN }
  return (
    <tr className="border-b last:border-0" style={{ borderColor: `${PBCC_GOLD}22` }}>
      <td className="py-2.5 pl-4 pr-2 text-left align-middle">
        <span className={`${aEmph ? 'font-black' : 'font-bold'} text-sm whitespace-nowrap`} style={{ color: aEmph ? GREEN : TV_CARD_TEXT }}>
          {marker ? <span className="mr-1" style={{ color: GREEN }}>{marker}</span> : null}{lastNames(teamA.info?.name)}
        </span>
      </td>
      <td className="py-2.5 px-1 text-center align-middle text-sm uppercase tracking-wide font-bold whitespace-nowrap" style={{ color: centerColor }}>
        {center}
      </td>
      <td className="py-2.5 pr-4 pl-2 text-right align-middle">
        <span className={`${aEmph ? 'font-medium' : 'font-bold'} text-sm whitespace-nowrap`} style={{ color: aEmph ? DIM : TV_CARD_TEXT }}>
          {lastNames(teamB.info?.name)}
        </span>
      </td>
    </tr>
  )
}

// Walk the flight, dedupe via opponentId so each pair shows as ONE row.
// Orient each pair so the leader sits in the Team A (left) column. Sort by
// lead size (largest leads first), then by holes played, so the most
// decisive matches surface at the top.
function buildMatches(teams) {
  const byId = Object.fromEntries(teams.map((t) => [t.id, t]))
  const seen = new Set()
  const matches = []
  for (const t of teams) {
    if (seen.has(t.id)) continue
    const oppId = t.info?.opponentId
    if (oppId && byId[oppId] && oppId !== t.id) {
      seen.add(t.id); seen.add(oppId)
      const opp = byId[oppId]
      const h2h = headToHeadStatus(t, opp)
      const gap = h2h.myWins - h2h.oppWins
      // Reorient so the leading team always sits in column A. matchPlayResult
      // gets myWins/oppWins from teamA's POV so its winner='me' means teamA
      // wins, simplifying the FINAL banner.
      let teamA, teamB, leaderGap, aWins, bWins
      if (gap >= 0) { teamA = t; teamB = opp; leaderGap = gap; aWins = h2h.myWins; bWins = h2h.oppWins }
      else { teamA = opp; teamB = t; leaderGap = -gap; aWins = h2h.oppWins; bWins = h2h.myWins }
      const result = matchPlayResult({ myWins: aWins, oppWins: bWins, played: h2h.played })
      matches.push({
        key: `${t.id}-${opp.id}`,
        teamA, teamB,
        leaderGap,
        played: h2h.played,
        result,
      })
    } else {
      seen.add(t.id)
      matches.push({ key: `solo-${t.id}`, teamA: t, teamB: null, leaderGap: 0, played: 0, result: null })
    }
  }
  matches.sort((a, b) => {
    // Finals first (decided matches), then by lead size, then by holes played.
    const af = a.result?.final ? 1 : 0
    const bf = b.result?.final ? 1 : 0
    if (af !== bf) return bf - af
    if (b.leaderGap !== a.leaderGap) return b.leaderGap - a.leaderGap
    if (b.played !== a.played) return b.played - a.played
    return 0
  })
  return matches
}

function MatchRow({ index, match, tv = false }) {
  const { teamA, teamB, leaderGap, played, result } = match
  const SUB = '#5B5B5B'   // muted player-name subline on cream
  const rowBorder = `${PBCC_GOLD}22`
  // Premium TEST TV declutter: drop the player sublines and emphasize the leader.
  const premium = tv && PBCC_PREMIUM

  if (!teamB) {
    return (
      <tr className="border-b last:border-0" style={{ borderColor: rowBorder }}>
        <td className="pl-3 pr-0 py-3 font-extrabold text-2xl align-middle" style={{ color: tv ? TV_CARD_TEXT : PBCC_CRIMSON }}>{index + 1}</td>
        <td colSpan={3} className="px-4 py-3 font-semibold align-middle" style={{ color: tv ? TV_CARD_TEXT : STATUS_MUTED }}>
          {lastNames(teamA.info?.name)} — no opponent assigned
        </td>
      </tr>
    )
  }

  const allSquare = leaderGap === 0
  const notStarted = played === 0
  const isFinal = !!result?.final
  const halved = isFinal && result?.winner === 'tie'

  // Names are always crimson (brand). The ▲/▼/◆ symbol + gold status text
  // carry the who's-winning cue so nothing is lost by dropping the old
  // green/red name coloring.
  if (isFinal) {
    // Match over: keep it short so it doesn't crowd out the Team B name —
    // just the winner + "WINS" (the margin like "1 UP" only shows while the
    // match is live). Halved matches read "HALVED".
    const winnerName = halved ? '' : (lastNames(teamA.info?.name) || 'Team A')
    // Premium TV: mirror the in-progress LEADER emphasis so finals read at a
    // glance too — winner (teamA) bold/larger with a green 🏆 (distinct from the
    // live ▲), result in green ("WINS" / "WINS 4&2"), loser dimmed. Non-premium
    // keeps the original "{winner} WINS" banner.
    const finalWinnerEmph = premium && !halved
    const rt = result?.resultText ? ` ${result.resultText}` : '' // e.g. " 4&2" / " 3 up"
    const banner = premium
      ? (halved ? 'HALVED' : `WINS${rt}`)
      : (halved ? 'HALVED' : `${winnerName} WINS`)
    return (
      <tr className="border-b last:border-0" style={{ borderColor: rowBorder, backgroundColor: tv ? 'transparent' : `${PBCC_GOLD}1f` }}>
        <td className="pl-3 pr-0 py-3 font-extrabold text-2xl align-middle" style={{ color: tv ? TV_CARD_TEXT : PBCC_CRIMSON }}>{index + 1}</td>
        <td className="px-2 py-3 text-center align-middle">
          <div className={finalWinnerEmph ? 'font-black text-xl leading-tight' : 'font-bold text-base'} style={{ color: tv ? TV_CARD_TEXT : PBCC_CRIMSON }}>
            {finalWinnerEmph ? <span style={{ color: '#0A5A2A' }}>🏆 </span> : null}{lastNames(teamA.info?.name)}
          </div>
          {!premium && <div className="text-sm" style={{ color: tv ? TV_CARD_TEXT : SUB }}>{(teamA.info?.players || []).join(' · ')}</div>}
        </td>
        <td className="px-1 py-3 text-center align-middle">
          <div className={`${finalWinnerEmph ? 'font-black' : 'font-extrabold'} text-sm uppercase tracking-wider whitespace-nowrap`} style={{ color: tv ? (premium ? (halved ? '#5B6B5B' : '#0A5A2A') : TV_CARD_TEXT) : STATUS_GREEN }}>
            {banner}
          </div>
        </td>
        <td className="px-2 py-3 text-center align-middle">
          <div className={finalWinnerEmph ? 'font-medium text-base' : 'font-bold text-base'} style={{ color: tv ? (finalWinnerEmph ? '#8C8C8C' : TV_CARD_TEXT) : PBCC_CRIMSON }}>
            {lastNames(teamB.info?.name)}
          </div>
          {!premium && <div className="text-sm" style={{ color: tv ? TV_CARD_TEXT : SUB }}>{(teamB.info?.players || []).join(' · ')}</div>}
        </td>
      </tr>
    )
  }

  let statusColor, statusText
  if (notStarted) {
    statusColor = STATUS_MUTED
    statusText = 'Not started'
  } else if (allSquare) {
    statusColor = STATUS_GREEN
    statusText = `ALL SQUARE THRU ${played}`
  } else {
    // teamA is the leader (oriented in buildMatches).
    statusColor = STATUS_GREEN
    statusText = `${leaderGap} UP THRU ${played}`
  }

  const rowBg = tv ? 'transparent' : (notStarted ? 'transparent' : index === 0 ? `${PBCC_GOLD}1f` : index % 2 ? 'rgba(18, 61, 38,0.05)' : 'transparent')

  // Premium TV: emphasize the LEADER (teamA is oriented as the leader in
  // buildMatches) with a heavier/larger name + a green ▲, and dim the trailer,
  // so a glance shows who's up. All-square / not-started emphasize neither side.
  const leaderEmph = premium && !allSquare && !notStarted
  const premStatusColor = (allSquare || notStarted) ? '#5B6B5B' : '#0A5A2A'
  return (
    <tr className="border-b last:border-0" style={{ borderColor: rowBorder, backgroundColor: rowBg }}>
      <td className="pl-3 pr-0 py-3 font-extrabold text-2xl align-middle" style={{ color: tv ? TV_CARD_TEXT : PBCC_CRIMSON }}>{index + 1}</td>
      <td className="px-2 py-3 text-center align-middle">
        <div className={leaderEmph ? 'font-black text-xl leading-tight' : 'font-bold text-base'} style={{ color: tv ? TV_CARD_TEXT : PBCC_CRIMSON }}>
          {leaderEmph ? <span style={{ color: '#0A5A2A' }}>▲ </span> : null}{lastNames(teamA.info?.name)}
        </div>
        {!premium && <div className="text-sm" style={{ color: tv ? TV_CARD_TEXT : SUB }}>{(teamA.info?.players || []).join(' · ')}</div>}
      </td>
      <td className="px-1 py-3 text-center align-middle">
        <div className={`${leaderEmph ? 'font-black' : 'font-extrabold'} text-base whitespace-nowrap`} style={{ color: tv ? (premium ? premStatusColor : TV_CARD_TEXT) : statusColor }}>
          {statusText}
        </div>
      </td>
      <td className="px-2 py-3 text-center align-middle">
        <div className={leaderEmph ? 'font-medium text-base' : 'font-bold text-base'} style={{ color: tv ? (leaderEmph ? '#8C8C8C' : TV_CARD_TEXT) : PBCC_CRIMSON }}>
          {lastNames(teamB.info?.name)}
        </div>
        {!premium && <div className="text-sm" style={{ color: tv ? TV_CARD_TEXT : SUB }}>{(teamB.info?.players || []).join(' · ')}</div>}
      </td>
    </tr>
  )
}

function ContestStrip({ contests }) {
  if (!contests) return null
  const items = []

  // Multiple KPs (current schema). Falls back to legacy singular `kp` for any
  // tournament created before the multi-KP migration.
  const kps = normalizeKps(contests)
  kps.forEach((k, i) => {
    if (!k?.winner) return
    items.push({
      key: `kp-${k.hole ?? i}`,
      label: `KP H${k.hole ?? '?'}`,
      value: `${k.winner}${k.distance ? ` · ${k.distance}` : ''}`,
    })
  })

  if (contests.longdrive?.winner) {
    items.push({
      key: 'ld',
      label: `Long Drive H${contests.longdrive.hole ?? '?'}`,
      value: `${contests.longdrive.winner}${contests.longdrive.distance ? ` · ${contests.longdrive.distance}` : ''}`,
    })
  }

  if (contests.longputt?.winner) {
    items.push({
      key: 'lp',
      label: `Longest Putt H${contests.longputt.hole ?? '?'}`,
      value: `${contests.longputt.winner}${contests.longputt.distance ? ` · ${contests.longputt.distance}` : ''}`,
    })
  }

  // Hole-in-One: show even before there's a winner so the prize/hole are
  // visible during the round; switch to "winner · ACE" once awarded.
  if (contests.hio && (contests.hio.prize || contests.hio.winner)) {
    items.push({
      key: 'hio',
      label: `Hole in One H${contests.hio.hole ?? '?'}`,
      value: contests.hio.winner
        ? `${contests.hio.winner} · ACE${contests.hio.prize ? ` · ${contests.hio.prize}` : ''}`
        : (contests.hio.prize || 'Prize on tap'),
    })
  }

  if (items.length === 0) return null
  return (
    <div className="mt-6 rounded-2xl p-4 flex flex-wrap gap-6 justify-center" style={{ backgroundColor: '#0F241A', border: `1px solid ${PBCC_GOLD}` }}>
      {items.map((it) => (
        <div key={it.key} className="text-center">
          <div className="text-sm uppercase tracking-widest" style={{ color: '#F0D890' }}>{it.label}</div>
          <div className="text-lg font-bold" style={{ color: '#FFFFFF' }}>{it.value}</div>
        </div>
      ))}
    </div>
  )
}

// kps may come back from RTDB as an array (dense) or an object keyed by index
// (sparse). Legacy tournaments stored a single `kp`. Normalize all three.
function normalizeKps(contests) {
  const raw = contests?.kps
  if (Array.isArray(raw)) return raw.filter(Boolean)
  if (raw && typeof raw === 'object') {
    return Object.keys(raw).sort().map((k) => raw[k]).filter(Boolean)
  }
  if (contests?.kp?.winner) return [contests.kp]
  return []
}

function BoardEmpty({ msg }) {
  return (
    <div className="min-h-full bg-green-deep text-white flex items-center justify-center p-12">
      <div className="text-center">
        <div className="text-6xl mb-3">⛳</div>
        <p className="text-xl">{msg}</p>
      </div>
    </div>
  )
}
